import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Grid,
  Chip,
  Fade,
  Paper,
  Container,
} from '@mui/material';
import {
  Person as PersonIcon,
  SupportAgent as AgentIcon,
  AdminPanelSettings as AdminIcon,
  AutoAwesome as MagicIcon,
} from '@mui/icons-material';
import { randomUserService } from '../services/randomUserService';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
  avatar?: string;
}

interface ProfileSelectionProps {
  onProfileSelect: (userData: User) => void;
}

interface DemoProfile {
  role: 'customer' | 'agent' | 'admin';
  title: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
  color: string;
  gradient: string;
  user: { name: string; avatar: string } | null;
}

const ProfileSelection: React.FC<ProfileSelectionProps> = ({ onProfileSelect }) => {
  const { user: auth0User, isAuthenticated } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<'customer' | 'agent' | 'admin' | null>(null);
  const [demoProfiles, setDemoProfiles] = useState<DemoProfile[]>([
    {
      role: 'customer',
      title: 'Customer Experience',
      description: 'Experience the platform from a customer\'s perspective with support chat and service requests.',
      features: ['AI Chat Support', 'Service Requests', 'Account Management', 'Network Status'],
      icon: <PersonIcon sx={{ fontSize: 40 }} />,
      color: '#1fb8cc',
      gradient: 'linear-gradient(135deg, #1fb8cc 0%, #21cbf3 100%)',
      user: null,
    },
    {
      role: 'agent',
      title: 'Support Agent',
      description: 'Work as a customer support agent with advanced AI tools and analytics capabilities.',
      features: ['Customer Analytics', 'AI Assistance', 'Ticket Management', 'Performance Metrics'],
      icon: <AgentIcon sx={{ fontSize: 40 }} />,
      color: '#95c100',
      gradient: 'linear-gradient(135deg, #95c100 0%, #a8d333 100%)',
      user: null,
    },
    {
      role: 'admin',
      title: 'Platform Administrator',
      description: 'Full platform access with system management, analytics, and administrative controls.',
      features: ['System Administration', 'Advanced Analytics', 'User Management', 'Network Operations'],
      icon: <AdminIcon sx={{ fontSize: 40 }} />,
      color: '#1e212a',
      gradient: 'linear-gradient(135deg, #1e212a 0%, #1e212a 100%)',
      user: null,
    },
  ]);

  // Generate demo users for each profile
  useEffect(() => {
    const generateDemoUsers = async () => {
      try {
        const [customer, agent, admin] = await Promise.all([
          randomUserService.getDemoUser('customer'),
          randomUserService.getDemoUser('agent'),
          randomUserService.getDemoUser('admin'),
        ]);

        setDemoProfiles(prev => prev.map(profile => ({
          ...profile,
          user: profile.role === 'customer' ? { name: customer.name, avatar: customer.avatar } :
                profile.role === 'agent' ? { name: agent.name, avatar: agent.avatar } :
                { name: admin.name, avatar: admin.avatar }
        })));
      } catch (error) {
        console.warn('Failed to generate demo users:', error);
        // Fallback users
        setDemoProfiles(prev => prev.map(profile => ({
          ...profile,
          user: profile.role === 'customer' ? { name: 'Sarah Johnson', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' } :
                profile.role === 'agent' ? { name: 'Alex Thompson', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' } :
                { name: 'Admin Smith', avatar: 'https://randomuser.me/api/portraits/men/65.jpg' }
        })));
      }
    };

    generateDemoUsers();
  }, []);

  const handleProfileSelect = (role: 'customer' | 'agent' | 'admin') => {
    setSelectedProfile(role);
    setLoading(true);

    const selectedDemoProfile = demoProfiles.find(p => p.role === role);
    
    // Create user object combining Auth0 info with selected role
    const userData: User = {
      id: auth0User?.sub || `demo_${role}_${Date.now()}`,
      name: selectedDemoProfile?.user?.name || auth0User?.name || `Demo ${role}`,
      email: auth0User?.email || `demo@procogia.com`,
      role: role,
      avatar: selectedDemoProfile?.user?.avatar || auth0User?.picture,
    };

    // Simulate brief loading for better UX
    setTimeout(() => {
      onProfileSelect(userData);
      setLoading(false);
    }, 1000);
  };

  const generateNewNames = async () => {
    try {
      const [customer, agent, admin] = await Promise.all([
        randomUserService.getDemoUser('customer'),
        randomUserService.getDemoUser('agent'),
        randomUserService.getDemoUser('admin'),
      ]);

      setDemoProfiles(prev => prev.map(profile => ({
        ...profile,
        user: profile.role === 'customer' ? { name: customer.name, avatar: customer.avatar } :
              profile.role === 'agent' ? { name: agent.name, avatar: agent.avatar } :
              { name: admin.name, avatar: admin.avatar }
      })));
    } catch (error) {
      console.warn('Failed to generate new demo users:', error);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1fb8cc 0%, #95c100 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Fade in timeout={600}>
          <Paper
            elevation={10}
            sx={{
              background: 'linear-gradient(135deg, #1fb8cc 0%, #95c100 100%)',
              color: 'white',
              p: 4,
              borderRadius: 4,
              mb: 4,
              textAlign: 'center',
            }}
          >
            <MagicIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              Welcome to ProCogia Telecom AI
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
              Choose your demo experience to explore the platform
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              Hello {auth0User?.name || auth0User?.email} 👋
            </Typography>
          </Paper>
        </Fade>

        {/* Profile Selection Cards */}
        <Grid container spacing={4}>
          {demoProfiles.map((profile, index) => (
            <Grid item xs={12} md={4} key={profile.role}>
              <Fade in timeout={800 + index * 200}>
                <Card
                  elevation={selectedProfile === profile.role ? 20 : 10}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: selectedProfile === profile.role ? 'scale(1.02)' : 'scale(1)',
                    border: selectedProfile === profile.role ? `3px solid ${profile.color}` : '3px solid transparent',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      elevation: 15,
                    },
                  }}
                  onClick={() => !loading && handleProfileSelect(profile.role)}
                >
                  <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Profile Header */}
                    <Box
                      sx={{
                        background: profile.gradient,
                        color: 'white',
                        p: 3,
                        borderRadius: 3,
                        textAlign: 'center',
                        mb: 3,
                      }}
                    >
                      {profile.icon}
                      <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                        {profile.title}
                      </Typography>
                    </Box>

                    {/* Demo User Info */}
                    {profile.user && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          p: 2,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 2,
                        }}
                      >
                        <Avatar
                          src={profile.user.avatar}
                          sx={{ width: 40, height: 40, mr: 2 }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {profile.user.name}
                          </Typography>
                          <Chip
                            label={profile.role.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: profile.color,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 3, flexGrow: 1 }}
                    >
                      {profile.description}
                    </Typography>

                    {/* Features */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Features:
                      </Typography>
                      {profile.features.map((feature, i) => (
                        <Typography
                          key={i}
                          variant="body2"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.5,
                            '&::before': {
                              content: '"•"',
                              color: profile.color,
                              fontWeight: 'bold',
                              width: '1em',
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>

                    {/* Select Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      sx={{
                        background: profile.gradient,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': {
                          background: profile.gradient,
                          filter: 'brightness(1.1)',
                        },
                      }}
                    >
                      {loading && selectedProfile === profile.role
                        ? 'Starting Experience...'
                        : `Experience as ${profile.title}`}
                    </Button>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Generate New Names Button */}
        <Fade in timeout={1200}>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={generateNewNames}
              disabled={loading}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              🎲 Generate New Random Names
            </Button>
          </Box>
        </Fade>

        {/* Footer */}
        <Fade in timeout={1400}>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Powered by Model Context Protocol (MCP) | ProCogia AI Excellence
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              🤖 Each experience generates authentic demo data with randomuser.me integration
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ProfileSelection; 